<div [class]="layout.shell">
  <header [class]="layout.header">
    <!-- The `!` is load-bearing: the global stylesheet sizes every `h2` with `!important`. -->
    <div class="flex items-center gap-2">
      <mat-icon class="h-5! w-5! shrink-0 text-xl! leading-5! text-primary-600">folder_managed</mat-icon>
      <h2
        id="case-form-title"
        class="min-w-0 flex-1 truncate text-lg! leading-5! font-semibold! text-neutral-800"
      >
        {{ (editing ? "guaranteeV2.action.editCase" : "guaranteeV2.action.createCase") | translate }}
      </h2>
    </div>
    <p class="pl-7 text-sm text-neutral-500">{{ "guaranteeV2.message.caseFormHint" | translate }}</p>
  </header>

  <div [class]="layout.body">
    <!-- Named rather than derived: the code identifies a diagnosis, the name tells the care story. -->
    <label class="flex min-w-0 flex-col gap-1">
      <span
        guaranteesLabel
        required
        [missing]="!name().trim()"
        >{{ "guaranteeV2.label.caseName" | translate }}</span
      >
      <input
        guaranteesControl
        class="w-full"
        name="caseName"
        type="text"
        autocomplete="off"
        [placeholder]="'guaranteeV2.message.caseNamePlaceholder' | translate"
        [ngModel]="name()"
        (ngModelChange)="name.set($event)"
      />
      <span [class]="fieldMessage">
        @if (!name().trim()) {
          {{ "guaranteeV2.message.caseNeedsName" | translate }}
        }
      </span>
    </label>

    <!-- The span, which is the decision: it is what admits a guarantee into this case at all. -->
    <label class="flex min-w-0 flex-col gap-1">
      <span
        guaranteesLabel
        required
        [missing]="periodMissing()"
        >{{ "guaranteeV2.label.casePeriod" | translate }}</span
      >
      <span class="flex items-center gap-1.5">
        <input
          guaranteesControl
          class="min-w-0 flex-1 tabular-nums"
          name="caseStartDate"
          type="date"
          [ngModel]="startDate()"
          (ngModelChange)="startDate.set($event)"
        />
        <span class="shrink-0 text-sm text-neutral-400">&ndash;</span>
        <input
          guaranteesControl
          class="min-w-0 flex-1 tabular-nums"
          name="caseEndDate"
          type="date"
          [ngModel]="endDate()"
          (ngModelChange)="endDate.set($event)"
        />
      </span>
      <span [class]="fieldMessage">
        @if (periodInverted()) {
          {{ "guaranteeV2.message.casePeriodInverted" | translate }}
        } @else if (periodMissing()) {
          {{ "guaranteeV2.message.caseNeedsPeriod" | translate }}
        }
      </span>
      <span class="text-sm text-neutral-500">{{ "guaranteeV2.message.casePeriodHint" | translate: { days: duration } }}</span>
    </label>

    <!-- Said before the write: shrinking a span never unfiles anything, so nothing is lost silently. -->
    @if (strandedCount(); as stranded) {
      <guarantees-callout
        tone="warning"
        icon="warning"
        >{{ "guaranteeV2.message.casePeriodStrands" | translate: { count: stranded } }}</guarantees-callout
      >
    }

    <!--
      The one diagnosis set the case has. Case search ranks on it, so a case with none could never
      be suggested for anything — which is why the form refuses one.
    -->
    <!--
      `min-w-0` is load-bearing: a fieldset carries `min-inline-size: min-content` from the UA
      stylesheet, so without it the block refuses to shrink below the widest diagnosis name it
      holds — the chips never get to truncate, and the dialog scrolls sideways instead.
    -->
    <fieldset class="flex min-w-0 flex-col gap-1.5 border-t border-neutral-100 pt-3">
      <legend
        guaranteesLabel
        required
        class="pb-1"
        [missing]="diagnoses().length === 0"
      >
        {{ "guaranteeV2.label.caseDiagnoses" | translate }}
      </legend>

      @if (diagnoses().length > 0) {
        <div class="flex flex-wrap gap-1">
          @for (coding of diagnoses(); track coding.code) {
            <guarantees-coding-chip
              [coding]="coding"
              (picked)="removeCoding($event)"
            ></guarantees-coding-chip>
          }
        </div>
      }

      <guarantees-icd10-picker
        [taken]="codes()"
        (selected)="addCoding($event)"
      ></guarantees-icd10-picker>

      <!-- The member's own codes first; the search above still reaches the whole dictionary. -->
      @let offered = suggestions();
      @if (offered.length > 0) {
        <span class="mt-0.5 text-sm text-neutral-400">{{ "guaranteeV2.message.diagnosesFromHistoryHint" | translate }}</span>
        <div class="flex flex-wrap gap-1">
          @for (coding of offered; track coding.code) {
            <guarantees-coding-chip
              action="add"
              [coding]="coding"
              (picked)="addCoding($event)"
            ></guarantees-coding-chip>
          }
        </div>
      }

      <span [class]="fieldMessage">
        @if (diagnoses().length === 0) {
          {{ "guaranteeV2.message.caseNeedsDiagnosis" | translate }}
        }
      </span>
    </fieldset>
  </div>

  <footer [class]="layout.footer">
    <button
      [class]="buttons.ghost.md"
      type="button"
      (click)="cancel()"
    >
      {{ "guaranteeV2.action.cancel" | translate }}
    </button>
    <button
      [class]="buttons.primary.md"
      type="button"
      [disabled]="!canSubmit()"
      (click)="submit()"
    >
      <mat-icon class="h-3.5! w-3.5! text-sm! leading-3.5!">check</mat-icon>
      {{ (editing ? "guaranteeV2.action.saveCase" : "guaranteeV2.action.createCase") | translate }}
    </button>
  </footer>
</div>
